import React from 'react';
import { Link } from '@inertiajs/react';
import { Card, Button, Table, Badge } from 'react-bootstrap';
import Valuation from '../../Data/Valuation';

const StatusBadge = ({ valuation }: { valuation: Valuation }) => {
	const status = valuation.status;
	if (!status) return <span className='text-muted'>—</span>;

	const displayStatus = status === 'In Progress' && !valuation.user_started_at
		? 'Not Started'
		: status;

	const colorMap: Record<string, string> = {
		'Not Started': 'blue',
		'In Progress': 'blue',
		'Under Review': 'dkRed',
		'Complete': 'primary',
	};
	const color = colorMap[displayStatus] ?? 'primary';

	return (
		<Badge
			bg=''
			className={`border border-${color} text-${color} px-3 fw-semibold d-inline-flex align-items-center`}
			style={{ height: '30px', backgroundColor: '#fff' }}
		>
			{displayStatus}
		</Badge>
	);
};

const ActionButtons = ({ valuation }: { valuation: Valuation }) => {
	switch (valuation.status) {
		case 'In Progress':
			return (
				<Button
					as={Link as any}
					href={`/valuation-form/${valuation.id}/basics`}
					variant='primary'
					size='sm'
					className='rounded-pill px-7'
				>
					{valuation.user_started_at ? 'Resume' : 'Begin'}
				</Button>
			);
		case 'Under Review':
			return (
				<Button
					as={Link as any}
					href={`/valuation-form/${valuation.id}/basics`}
					variant='secondary'
					size='sm'
					className='rounded-pill px-7'
				>
					View Form
				</Button>
			);
		case 'Complete':
			return (
				<div className='d-flex gap-2'>
					<Button
						as={Link as any}
						href={`/valuation-form/${valuation.id}/basics`}
						variant='secondary'
						size='sm'
						className='rounded-pill px-7'
					>
						View Form
					</Button>
					{valuation.has_report && (
						<Button
							as='a'
							href={`/valuation/${valuation.id}/download-report`}
							target='_blank'
							rel='noopener noreferrer'
							variant='primary'
							size='sm'
							className='rounded-pill px-7'
						>
							Download Report
						</Button>
					)}
				</div>
			);
		default:
			return null;
	}
};

interface Props {
	valuations: Valuation[];
}

const ValuationsTableCard = ({ valuations }: Props) => {
	return (
		<Card>
			<Card.Body className='px-7 py-9'>
				<h5 className='fw-bold mb-4'>
					Your Valuations
				</h5>
				{valuations.length > 0 ? (
					<Table striped borderless hover responsive>
						<thead>
							<tr>
								<th>Effective Date</th>
								<th>Status</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{valuations.map((valuation) => (
								<tr key={valuation.id}>
									<td className='align-middle'>
										{new Date(valuation.effective_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
									</td>
									<td>
										<StatusBadge
											valuation={valuation}
										/>
									</td>
									<td>
										<ActionButtons
											valuation={valuation}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				) : (
					<p className='text-muted'>
						No valuations found for your agency.
					</p>
				)}
			</Card.Body>
		</Card>
	);
};

export default ValuationsTableCard;
